import React, { useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["processing", "shipped", "delivered", "cancelled"];

export default function StickerOrderEditModal({ order, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    tracking_number: order?.tracking_number || "",
    carrier: order?.carrier || "",
    order_status: order?.order_status || "processing",
    admin_notes: order?.admin_notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/stickers/orders/${order.id}`, form);
      toast.success("Order updated");
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sticker-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Update sticker order</DialogTitle>
          <DialogDescription>
            Track tracking numbers and shipment status manually.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">Tracking number</Label>
              <Input
                value={form.tracking_number}
                onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                className="h-11 font-mono"
                placeholder="9400 1234 5678 ..."
                data-testid="sticker-tracking-number"
              />
            </div>
            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">Carrier</Label>
              <Input
                value={form.carrier}
                onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                className="h-11"
                placeholder="USPS / UPS / FedEx"
                data-testid="sticker-carrier"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="mono-tiny text-[#71717A]">Status</Label>
            <Select
              value={form.order_status}
              onValueChange={(v) => setForm({ ...form, order_status: v })}
            >
              <SelectTrigger className="h-11" data-testid="sticker-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="mono-tiny text-[#71717A]">Admin notes</Label>
            <Textarea
              rows={3}
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              data-testid="sticker-admin-notes"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="h-11" data-testid="sticker-edit-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
            data-testid="sticker-edit-save"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
